import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useMatchmakingStore,
  useAuthStore,
  typedOn,
  typedOff,
  type MatchFoundPayload,
} from '@fog-chess/core';
import { GAME_SOCKET_EVENTS } from '@/lib/socket';

/**
 * Type guard for the match:found payload. The core socket emits via the
 * untyped convenience path, so we defensively narrow before trusting fields.
 */
function isMatchFoundPayload(value: unknown): value is MatchFoundPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { match_id?: unknown }).match_id === 'string' &&
    typeof (value as { opponent_id?: unknown }).opponent_id === 'string' &&
    typeof (value as { opponent_username?: unknown }).opponent_username ===
      'string'
  );
}

/**
 * GlobalMatchmakingListener - Mandate B.
 *
 * Mounted exactly once inside RootLayout. It listens to the singleton socket
 * for `match:found` / `match:started` and force-navigates the user into
 * `/game/:matchId` regardless of which screen they're currently on. This
 * closes the race where a user clicks "Search" in QuickMatchScreen then
 * navigates to Profile while still queued in Redis.
 *
 * Why singleton socket instead of the per-component hook: a per-component
 * listener is torn down (and re-created) on every navigation, which would
 * miss the found event fired while the user is mid-transition. The singleton
 * socket survives navigation and the listener here is registered for the
 * entire authenticated session lifetime.
 */
export function GlobalMatchmakingListener(): null {
  const navigate = useNavigate();
  const location = useLocation();
  const matchFound = useMatchmakingStore((s) => s.setMatchFound);
  const clearAiFallback = useMatchmakingStore((s) => s.clearAiFallback);
  const isAuthenticated = useAuthStore(
    (s) => !!s.accessToken && !!s.user,
  );

  // Track whether we've already auto-routed for a given match id so StrictMode
  // double-invoke + duplicate server pushes can't bounce the user twice.
  const routedMatchId = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      routedMatchId.current = null;
      return;
    }

    const handleMatchFound = (payload: unknown) => {
      if (!isMatchFoundPayload(payload)) return;
      if (routedMatchId.current === payload.match_id) return;
      routedMatchId.current = payload.match_id;

      // Persist the found match so QuickMatchScreen (if still mounted) and
      // the upcoming game screen can read the opponent payload synchronously.
      matchFound(payload);
      clearAiFallback();

      const target = `/game/${payload.match_id}`;
      // Avoid creating a duplicate history entry / no-op navigation.
      if (location.pathname !== target) {
        navigate(target, { replace: true });
      }
    };

    const handleMatchStarted = (payload: unknown) => {
      const maybe = payload as { match_id?: unknown } | null;
      if (maybe && typeof maybe.match_id === 'string') {
        if (routedMatchId.current === maybe.match_id) return;
        routedMatchId.current = maybe.match_id;
        const target = `/game/${maybe.match_id}`;
        if (location.pathname !== target) {
          navigate(target, { replace: true });
        }
      }
    };

    typedOn(GAME_SOCKET_EVENTS.MATCH_FOUND, handleMatchFound);
    typedOn(GAME_SOCKET_EVENTS.MATCH_STARTED, handleMatchStarted);

    return () => {
      typedOff(GAME_SOCKET_EVENTS.MATCH_FOUND, handleMatchFound);
      typedOff(GAME_SOCKET_EVENTS.MATCH_STARTED, handleMatchStarted);
    };
  }, [
    isAuthenticated,
    navigate,
    location.pathname,
    matchFound,
    clearAiFallback,
  ]);

  return null;
}
